import { Box, Column, Row, Spacer, Stack } from 'src/components-v2/Layout';
import { CmsThemes } from 'src/lib/constants';
import { MainTemplate } from 'src/templates';
import styled from 'threads5/themes/styled';
import type { IJobsDetailTemplateProps } from '../CareersSearch.types';
import CareersSearchItem from '../CareersSearchItem';
import JobDetailHeader from './JobDetailHeader';

const JobDetailTemplate = (props: IJobsDetailTemplateProps) => {
  const { metaData, job, intro, otherJobs } = props;

  return (
    <MainTemplate
      {...metaData}
      cmsTheme={CmsThemes.WEB3}
      data-id='job-post'
      sx={{
        overflow: 'visible',
        '#main-content': {
          overflow: 'visible',
        },
      }}
    >
      <Box
        sx={(theme) => {
          return {
            overflow: 'visible',
            background: theme.palette['gradient/background/subtle/elizabeth'],
          };
        }}
      >
        <Row>
          <Column xs={24} sm={24} md={12}>
            <JobDetailHeader job={job} />
          </Column>
          <Column
            xs={24}
            sm={24}
            md={10}
            mdOffset={1}
            sx={{
              px: {
                xs: '13px',
                sm: '70px',
                md: '0',
              },
            }}
          >
            <Spacer
              height={0}
              sx={{
                height: {
                  xs: '50px',
                  sm: '60px',
                  md: '177px',
                },
              }}
            />
            <Stack
              sx={{
                gap: {
                  xs: 5,
                  sm: 6,
                  md: 7,
                },
                mr: {
                  xl: 'calc(50vw - 686px - 76px)',
                },
              }}
            >
              {intro && <TitleContent as='div'>{intro}</TitleContent>}
              {job.description && (
                <BodyContent
                  dangerouslySetInnerHTML={{ __html: job.description }}
                />
              )}
              {Array.isArray(job.lists) &&
                job.lists.length > 0 &&
                job.lists.map((list) => {
                  return (
                    <Stack key={list.text} gap={2}>
                      <TitleContent>{list.text}</TitleContent>
                      <BodyContent>
                        <Stack
                          component={'ul'}
                          gap={1}
                          dangerouslySetInnerHTML={{ __html: list.content }}
                        />
                      </BodyContent>
                    </Stack>
                  );
                })}
              <BodyContent>
                {job.salary && (
                  <>
                    {job.salary}
                    <div>
                      <br />
                    </div>
                  </>
                )}
                {!job.salary && job.salaryDescription && (
                  <BodyContent
                    dangerouslySetInnerHTML={{
                      __html: job.salaryDescription,
                    }}
                  />
                )}
              </BodyContent>
              {job.additional && (
                <BodyContent
                  dangerouslySetInnerHTML={{
                    __html: job.additional,
                  }}
                />
              )}
              {Array.isArray(otherJobs) && otherJobs.length > 0 && (
                <Stack gap={2}>
                  <TitleContent>Other opportunities</TitleContent>
                  <Stack gap={3} component={'ul'} sx={{ ml: 0 }}>
                    {otherJobs.map((j) => {
                      return (
                        <Box
                          key={j.key}
                          component={'li'}
                          sx={{ listStyle: 'none' }}
                        >
                          <CareersSearchItem job={j} />
                        </Box>
                      );
                    })}
                  </Stack>
                </Stack>
              )}
              <Spacer
                height={0}
                sx={{
                  height: {
                    xs: '50px',
                    sm: '60px',
                    md: '82px',
                  },
                }}
              />
            </Stack>
          </Column>
        </Row>
      </Box>
    </MainTemplate>
  );
};

const TitleContent = styled('h2')(({ theme }) => {
  return {
    fontFamily: theme.typography.plaidSans,
    fontSize: '22px',
    fontWeight: 600,
    lineHeight: '120%',
    color: theme.palette['base/sky/600'],
    margin: 0,
    [theme.breakpoints.up('md')]: {
      fontSize: '30px',
    },
  };
});

const BodyContent = styled('div')(({ theme }) => {
  return {
    fontFamily: theme.typography.cern,
    fontSize: '1.6rem',
    lineHeight: '150%',
    a: {
      textDecoration: 'underline',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.8rem',
    },
    'br.ProseMirror-trailingBreak': {
      display: 'none',
    },
    p: {
      marginBottom: '2.7rem',
    },
    'p:empty': {
      display: 'none',
    },
    ul: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.8rem',
      marginBottom: '3.2rem',
    },
    'ul p': {
      marginBottom: 0,
    },
    'h1, h2, h3': {
      fontFamily: theme.typography.plaidSans,
      fontWeight: 600,
      lineHeight: '120%',
      color: theme.palette['base/sky/600'],
      margin: '5.6rem 0 1.6rem',
      display: 'block',
    },
    h1: {
      fontSize: '2.4rem',
      [theme.breakpoints.up('md')]: {
        fontSize: '3.2rem',
      },
    },
    h2: {
      fontSize: '2.2rem',
      [theme.breakpoints.up('md')]: {
        fontSize: '3rem',
      },
    },
    h3: {
      fontSize: '2rem',
      [theme.breakpoints.up('md')]: {
        fontSize: '2.8rem',
      },
    },
  };
});

export default JobDetailTemplate;
